import passwordPolicyHelper from "@/core/helpers/passwordPolicyHelper";
import {computed, ref} from "vue";

interface PasswordCheck {
    check: (password?: string) => boolean;
    label: string;
}

const passwordChecks: PasswordCheck[] = [
    {
        check: password => !passwordPolicyHelper.hasMinimumLength(password),
        label: `au moins ${passwordPolicyHelper.minLength} caractères`
    },
    {
        check: password => !passwordPolicyHelper.hasNumber(password),
        label: 'un nombre'
    },
    {
        check: password => !passwordPolicyHelper.hasLowercase(password),
        label: 'un caractère minuscule'
    },
    {
        check: password => !passwordPolicyHelper.hasUppercase(password),
        label: 'un caractère majuscule'
    },
    {
        check: password => !passwordPolicyHelper.hasSpecial(password),
        label: 'un caractère spécial'
    },
];

/**
 * Composable to handle password input, validation, and confirmation during the registration process.
 */
const usePassword = () => {
    const password = ref<string | undefined>(undefined);
    const passwordConfirmation = ref<string | undefined>(undefined);

    /**
     * Determine if the password is valid by checking it against the defined password policy.
     */
    const isPasswordValid = computed(() => {
        return passwordPolicyHelper.isValid(password.value);
    });

    /**
     * Get the password hint information based on the password validity and the specific requirements that are not met.
     */
    const passwordInfo = computed(() => {
        if (isPasswordValid.value) {
            return "Votre mot de passe est sécurisé 👍";
        }

        const missing = passwordChecks
            .filter(({check}) => check(password.value))
            .map(({label}) => label);

        return `Votre mot de passe doit contenir ${missing.join(', ')}.`;
    });

    /**
     * Determine if the password and its confirmation match.
     */
    const passwordsMatch = computed(() => {
        return password.value === passwordConfirmation.value;
    });

    return {
        password,
        passwordConfirmation,
        isPasswordValid,
        passwordInfo,
        passwordsMatch
    };
};

export default usePassword;
